<template>
  <div>
    <div
      v-if="enabledPlatforms.length > 0 && showFooter && props.reserveSpace"
      aria-hidden="true"
      class="md:tw-hidden tw-h-20 tw-shrink-0"
    />

    <!-- Regular grid display for inline use -->
    <div
      class="tw-grid tw-grid-cols-12 tw-gap-3"
      :class="props.inlineGridClass"
    >
      <div
        v-for="platform in enabledPlatforms"
        :key="platform.type"
        :class="[
          platform.type !== 'custom' && socialCount > 1
            ? 'tw-col-span-6'
            : 'tw-col-span-12',
          platform.type === 'custom' && 'tw-col-span-12',
          getInlineItemClass(platform),
        ]"
      >
        <ButtonExternalLink
          :title="getInlineTitle(platform)"
          :icon="platform.icon"
          :link="platform.link"
          :variant="platform.variant"
          :outline="platform.type !== 'custom'"
          class="tw-px-2 tw-h-full !tw-shadow-none"
          :title-class="titleClass || 'tw-text-mb-button-small lg:tw-text-dt-button-small'"
          :button-class="getButtonClass(platform)"
          @click="eventDetailUrlButton(platform.link)"
        />
      </div>
    </div>

    <!-- Fixed Footer display similar to CTABottomBar.client.vue -->
    <Teleport to="body">
      <Transition name="slide-up">
        <div
          v-if="enabledPlatforms.length > 0 && showFooter"
          class="cta-footer fast-mobile-fixed-footer md:tw-hidden tw-bg-white tw-fixed tw-bottom-0 tw-left-0 tw-right-0 tw-z-50"
        >
          <div
            class="tw-grid tw-grid-cols-6 tw-gap-2 tw-p-2"
            :class="props.footerClass"
          >
            <!--
            Mobile footer layout follows these rules:
            - If only 1 enabled button: expand to the full footer
            - If 2 enabled buttons with 'custom' button: 1/3 for social, 2/3 for custom
            - If 2 enabled buttons (both social): half-half for each
            - If 1 custom + 2+ social buttons: show collapse button (1/3) + custom button (2/3)
          -->

            <!-- Collapse Button for showing bottom sheet (only for 2+ social buttons) -->
            <div v-if="showCollapseCTA" class="tw-col-span-2">
              <ButtonPrimary
                icon="forum"
                :color="collapseButtonVariant"
                :outline="collapseButtonOutline"
                class="cta-collapse-button tw-w-full !tw-h-12"
                :class="buttonClass"
                @click.prevent="showBottomSheet"
              >
                {{ $t("global.contact") }}
              </ButtonPrimary>
            </div>

            <!-- Button rendering for custom and social buttons -->
            <template v-for="platform in enabledPlatforms" :key="platform.type">
              <ButtonExternalLink
                v-if="shouldDisplayInFooter(platform)"
                :class="getFooterClass(platform)"
                :title="getFooterClass(platform) === 'tw-col-span-6' || platform.type === 'custom' ? platform.title : (platform.shortTitle || platform.title)"
                :icon="platform.icon"
                :link="platform.link"
                :variant="platform.variant"
                icon-size="22px"
                :outline="callToActionButtonComputeOutline(platform)"
                :button-class="`${getButtonClass(platform)} !tw-h-12 !tw-shadow-none`"
                :title-class="titleClass || undefined"
                @click="eventDetailUrlButton(platform.link)"
              />
            </template>
          </div>
        </div>
      </Transition>
    </Teleport>
  </div>

  <!-- Dialog for social buttons -->
  <QDialog
    v-model="showDialog"
    :class="props.bottomSheetClass"
    position="bottom"
    full-width
  >
    <QCard
      class="tw-bg-white tw-w-full tw-max-w-sm"
      :class="bottomSheetClass"
      :style="bottomSheetCardStyle"
    >
      <QCardSection>
        <div
          class="tw-flex tw-items-center tw-justify-between tw-mb-6"
          :class="bottomSheetHeaderClass"
        >
          <div class="tw-text-lg">
            {{ props.bottomSheetTitle || t("global.contact") }}
          </div>
          <QBtn flat round icon="close" @click="showDialog = false" />
        </div>
        <!-- Use the same grid layout as the regular display -->
        <div
          class="tw-grid tw-grid-cols-12 tw-gap-2"
          :class="bottomSheetGridClass"
        >
          <div
            v-for="platform in enabledPlatforms"
            :key="platform.type"
            :class="[
              platform.type !== 'custom' && socialCount > 1
                ? 'tw-col-span-6'
                : 'tw-col-span-12',
              platform.type === 'custom' && 'tw-col-span-12',
            ]"
          >
            <ButtonExternalLink
              :title="platform.title"
              :icon="platform.icon"
              :link="platform.link"
              :variant="platform.variant"
              :outline="platform.type !== 'custom'"
              class="tw-px-2 tw-h-full !tw-shadow-none"
              :title-class="titleClass || 'tw-text-mb-button-small lg:tw-text-dt-button-small'"
              :button-class="getButtonClass(platform)"
              @click="eventDetailUrlButton(platform.link)"
            />
          </div>
        </div>
      </QCardSection>
    </QCard>
  </QDialog>
</template>

<script setup lang="ts">
import { QDialog, QCard, QCardSection, QSeparator, QBtn } from "quasar";
import type { VueClassProp } from "quasar";

defineOptions({ inheritAttrs: false });

// Map legacy builder icon names (stored in backoffice as `icon-{name}`)
// to their Material Symbols Outlined equivalents used by Quasar.
const LEGACY_ICON_MAP: Record<string, string> = {
  "icon-telegram-plane": "sym_o_send",
  "icon-hand-point-right": "sym_o_touch_app",
  "icon-hand-point-right1": "sym_o_touch_app",
  "icon-hand-point-up": "sym_o_arrow_upward",
  "icon-thumbs-up": "sym_o_thumb_up",
  "icon-arrow-circle-right": "sym_o_arrow_circle_right",
  "icon-bookmark": "sym_o_bookmark",
  "icon-bullhorn": "sym_o_campaign",
  "icon-calendar-alt": "sym_o_calendar_today",
  "icon-calendar-plus": "sym_o_event",
  "icon-cart-arrow-down": "sym_o_add_shopping_cart",
  "icon-cart-plus": "sym_o_add_shopping_cart",
  "icon-chevron-circle-right": "sym_o_chevron_right",
  "icon-comment-dollar": "sym_o_paid",
  "icon-comment-dots": "sym_o_chat_bubble",
  "icon-comments-dollar": "sym_o_monetization_on",
  "icon-external-link-alt": "sym_o_open_in_new",
  "icon-info": "sym_o_info",
  "icon-map-marked-alt": "sym_o_map",
  "icon-map-marker-alt": "sym_o_location_on",
  "icon-play-circle": "sym_o_play_circle",
  "icon-random": "sym_o_shuffle",
  "icon-share-alt": "sym_o_share",
  "icon-share-square": "sym_o_share",
  "icon-shopping-bag": "sym_o_shopping_bag",
  "icon-shopping-basket": "sym_o_shopping_basket",
  "icon-shopping-cart": "sym_o_shopping_cart",
  "icon-store": "sym_o_store",
  "icon-video": "sym_o_videocam",
};

const mapLegacyIcon = (icon: string): string => LEGACY_ICON_MAP[icon] ?? icon;

const props = withDefaults(
  defineProps<{
    enableButton: CallToActionButton;
    eventName?: string;

    collapseButtonVariant?: string;
    collapseButtonOutline?: boolean;

    bottomSheetTitle?: string;
    bottomSheetClass?: VueClassProp;
    bottomSheetActionClass?: VueClassProp;
    bottomSheetComputeActionClass?: (platform: ProductCallToAction) => string;
    buttonComputeClass?: (platform: ProductCallToAction) => string;

    callToActionButtonComputeOutline?: (
      platform: ProductCallToAction,
    ) => boolean;
    buttonClass?: string;
    titleClass?: string;
    hideSocialTitle?: boolean;
    footerClass?: string;
    bottomSheetCardStyle?: Record<string, string>;
    bottomSheetHeaderClass?: string;
    bottomSheetGridClass?: string;
    showFooter?: boolean;
    inlineGridClass?: string;
    inlineItemComputeClass?: (platform: ProductCallToAction) => string;
    inlineTitleCompute?: (platform: ProductCallToAction) => string;
    reserveSpace?: boolean;
  }>(),
  {
    eventName: "",

    collapseButtonOutline: true,
    collapseButtonVariant: "primary",

    bottomSheetClass: undefined,
    bottomSheetTitle: "",
    bottomSheetActionClass: undefined,
    bottomSheetComputeActionClass: () => "",
    buttonComputeClass: () => "",
    buttonClass: "",
    titleClass: "",
    inlineGridClass: "",
    inlineItemComputeClass: () => "",
    inlineTitleCompute: (platform: ProductCallToAction) => platform.title,

    showFooter: true,
    reserveSpace: true,

    callToActionButtonComputeOutline: (platform: ProductCallToAction) => {
      return platform.type !== "custom";
    },
  },
);

// Initialize Quasar plugin
const $q = useQuasar();
const {
  enabledPlatforms: rawPlatforms,
  socialCount,
  eventDetailUrlButton,
} = useCallToAction(props);

const enabledPlatforms = computed(() =>
  rawPlatforms.value.map((p) =>
    p.type === "custom" ? { ...p, icon: mapLegacyIcon(p.icon) } : p,
  ),
);

// Determine if we should show the collapse button
const showCollapseCTA = computed(() => {
  // Show collapse button only if there's at least one custom button AND more than one social button
  return (
    socialCount.value > 1 &&
    enabledPlatforms.value.some((p) => p.type === "custom")
  );
});

/**
 * Determine if a button should be displayed in the footer
 */
function shouldDisplayInFooter(platform: ProductCallToAction): boolean {
  // Always show custom button
  if (platform.type === "custom") {
    return true;
  }

  // Show social button only if there's exactly one social button or no collapse
  return !showCollapseCTA.value && socialCount.value > 0;
}

/**
 * Determine the appropriate class for a button in the footer grid
 * based on the platform type and total number of buttons
 */
function getFooterClass(platform: ProductCallToAction): string {
  // 1. If only 1 button total: it takes full width
  if (enabledPlatforms.value.length === 1) {
    return "tw-col-span-6"; // Full width
  }

  // 2. If collapse button is visible (1 custom + 2+ social buttons)
  if (showCollapseCTA.value) {
    // Collapse button takes 2 columns, custom button takes remaining 4 columns
    if (platform.type === "custom") {
      return "tw-col-span-4"; // 2/3 width
    }
  } else if (enabledPlatforms.value.length === 2) {
    // 3. If exactly 2 buttons total
    // 3a. If one is custom and one is social
    if (hasSocialAndCustom() && socialCount.value === 1) {
      if (platform.type === "custom") {
        return "tw-col-span-4"; // 2/3 width for custom
      } else {
        return "tw-col-span-2"; // 1/3 width for social
      }
    } else if (socialCount.value === 2) {
      // 3b. If both are social buttons
      return "tw-col-span-3"; // Half-half (3 columns each)
    }
  }

  // Default case - this should not be reached with proper conditions
  return platform.type === "custom" ? "tw-col-span-4" : "tw-col-span-2";
}

// Helper to check if we have both social and custom buttons
const hasSocialAndCustom = (): boolean => {
  return (
    socialCount.value > 0 &&
    enabledPlatforms.value.some((p) => p.type === "custom")
  );
};

// Dialog control
const { t } = useI18n();
const showDialog = ref(false);

// Get social platforms (non-custom buttons)
const socialPlatforms = computed(() => {
  return enabledPlatforms.value.filter((p) => p.type !== "custom");
});

// Show dialog with contact options
const showBottomSheet = (): void => {
  // Don't show dialog if no social platforms
  if (socialPlatforms.value.length === 0) {
    return;
  }

  // Show dialog instead of bottom sheet
  showDialog.value = true;
};

function getButtonClass(platform: ProductCallToAction): string {
  return [props.buttonClass, props.buttonComputeClass(platform)]
    .filter(Boolean)
    .join(" ");
}

function getInlineItemClass(platform: ProductCallToAction): string {
  return props.inlineItemComputeClass(platform);
}

function getInlineTitle(platform: ProductCallToAction): string {
  if ($q.screen.lt.md && platform.type !== "custom") {
    return platform.shortTitle || t("global.chat");
  }

  return props.inlineTitleCompute(platform);
}
</script>

<style scoped>
.slide-up-enter-active,
.slide-up-leave-active {
  transition: transform 0.3s ease;
}

.slide-up-enter-from,
.slide-up-leave-to {
  transform: translateY(100%);
}

:deep(.cta-collapse-button.q-btn) {
  background: #ffffff !important;
  border: 1.5px solid var(--theme-color-primary) !important;
  color: var(--theme-color-primary) !important;
}

:deep(.cta-collapse-button.q-btn::before) {
  border: 0 !important;
}

:deep(.cta-collapse-button .q-btn__content) {
  color: inherit !important;
}

:deep(.cta-collapse-button .q-icon) {
  color: inherit !important;
  font-size: 20px !important;
}

:deep(.cta-collapse-button .tw-text-dt-button) {
  color: inherit !important;
  font-size: 16px !important;
  line-height: 24px !important;
  font-weight: 400 !important;
}
</style>
